import type { SxProps, Theme } from "@mui/material";

export type HandlingTone = "claim" | "mine" | "other";

export const HANDLING_COPY = {
  claimAction: "קח לטיפול",
  releaseAction: "שחרר טיפול",
  mineStatus: "בטיפול שלי",
  otherStatus: (name: string) => `בטיפול של ${name}`,
  since: (time: string) => `מאז ${time}`,
  releaseOtherConfirm: (name: string) =>
    `לשחרר את הטיפול של ${name}?`,
  entryNotice: (name: string) =>
    `משימה זו בטיפול של ${name}. ניתן לצפות; שינויים מומלץ לתאם עם המטפל/ת.`,
  claimError: "לא ניתן לסמן את המשימה בטיפול",
  releaseError: "לא ניתן לשחרר את הטיפול",
} as const;

export function formatHandlingTimeShort(
  handlingSince?: Date | string | null,
): string | null {
  if (!handlingSince) return null;
  return new Date(handlingSince).toLocaleTimeString("he-IL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatHandlingTimeFull(
  handlingSince?: Date | string | null,
): string | null {
  if (!handlingSince) return null;
  return new Date(handlingSince).toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function handlingSinceTooltip(
  handlingSince?: Date | string | null,
): string | undefined {
  const full = formatHandlingTimeFull(handlingSince);
  return full ? `בטיפול ${full}` : undefined;
}

export const handlingActionButtonSx: SxProps<Theme> = {
  textTransform: "none",
  fontWeight: 500,
  borderRadius: "1rem",
  minHeight: "2.25rem",
  px: 1.5,
  whiteSpace: "nowrap",
  fontSize: "0.875rem",
};

export const handlingReleaseButtonSx: SxProps<Theme> = {
  textTransform: "none",
  fontWeight: 500,
  color: "text.secondary",
  minWidth: "auto",
  minHeight: "1.75rem",
  px: 0.75,
  whiteSpace: "nowrap",
  fontSize: "0.8125rem",
  "&:hover": {
    color: "text.primary",
    bgcolor: "action.hover",
  },
};

const HANDLING_PILL_TONE_STYLES = {
  claim: {
    borderColor: "divider",
    bgcolor: "background.paper",
  },
  mine: {
    borderColor: "rgba(0, 180, 216, 0.35)",
    bgcolor: "rgba(0, 180, 216, 0.08)",
  },
  other: {
    borderColor: "divider",
    bgcolor: "rgba(5, 32, 76, 0.04)",
  },
} as const satisfies Record<HandlingTone, object>;

export function handlingPillSx(
  tone: HandlingTone,
  size: "sm" | "md" = "md",
): SxProps<Theme> {
  const isSm = size === "sm";

  return {
    direction: "row",
    alignItems: "center",
    gap: isSm ? 0.5 : 0.75,
    px: isSm ? 1 : 1.5,
    py: isSm ? 0.375 : 0.625,
    borderRadius: isSm ? "0.75rem" : "1rem",
    border: "1px solid",
    maxWidth: "100%",
    minHeight: isSm ? "1.875rem" : "2.25rem",
    boxSizing: "border-box",
    ...HANDLING_PILL_TONE_STYLES[tone],
  } as SxProps<Theme>;
}

export function handlingIconColor(tone: HandlingTone): string {
  if (tone === "mine") return "primary.main";
  return "text.secondary";
}
